import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';

const AITipsContext = createContext();

export function AITipsProvider({ children }) {
  const [showTips, setShowTips] = useState(false);
  const [currentTip, setCurrentTip] = useState(null);
  const [tipHistory, setTipHistory] = useState([]);
  const [hasSeenTips, setHasSeenTips] = useState({});
  const [showOnboardingGuide, setShowOnboardingGuide] = useState(false);
  const location = useLocation();
  const { userDetails } = useAuth();

  // Load tip viewing history from sessionStorage (persists only during current session)
  useEffect(() => {
    const savedHistory = sessionStorage.getItem('aiTipsHistory');
    if (savedHistory) {
      setHasSeenTips(JSON.parse(savedHistory));
    }
  }, []);

  // Auto-show tips on first visit to a page - DISABLED
  // Tips will only show when user clicks the AI tips button
  // useEffect(() => {
  //   const pagePath = location.pathname;
  //   if (!hasSeenTips[pagePath]) {
  //     // Small delay to let the page render first
  //     const timer = setTimeout(() => {
  //       setShowTips(true);
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [location.pathname, hasSeenTips]);

  // Show onboarding guide on first login, scoped per user email
  useEffect(() => {
    if (!userDetails?.email) return;
    const key = `hasSeenOnboarding_${userDetails.email}`;
    if (!localStorage.getItem(key)) {
      const timer = setTimeout(() => {
        setShowOnboardingGuide(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userDetails]);

  const toggleTips = () => {
    setShowTips(!showTips);
  };

  const markTipAsSeen = (pagePath) => {
    const updatedHistory = {
      ...hasSeenTips,
      [pagePath]: true
    };
    setHasSeenTips(updatedHistory);
    sessionStorage.setItem('aiTipsHistory', JSON.stringify(updatedHistory));
  };

  const completeOnboarding = () => {
    if (userDetails?.email) {
      localStorage.setItem(`hasSeenOnboarding_${userDetails.email}`, 'true');
    }
    setShowOnboardingGuide(false);
  };

  const closeTips = () => {
    setShowTips(false);
    markTipAsSeen(location.pathname);
  };

  const resetTipsHistory = () => {
    setHasSeenTips({});
    sessionStorage.removeItem('aiTipsHistory');
  };

  const value = {
    showTips,
    setShowTips,
    toggleTips,
    closeTips,
    currentTip,
    setCurrentTip,
    tipHistory,
    setTipHistory,
    hasSeenTips,
    markTipAsSeen,
    resetTipsHistory,
    showOnboardingGuide,
    completeOnboarding
  };

  return (
    <AITipsContext.Provider value={value}>
      {children}
    </AITipsContext.Provider>
  );
}

export function useAITips() {
  const context = useContext(AITipsContext);
  if (!context) {
    throw new Error('useAITips must be used within AITipsProvider');
  }
  return context;
}
